import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import { authApi } from '../../services/authApi';
import { useAuthStore } from '../../stores/authStore';
import { ApiError } from '../../services/api';

const LoginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Please enter a valid email address.'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFields = z.infer<typeof LoginSchema>;

/**
 * Maps API failures to plain-language UX strings.
 *
 * The auth service returns 401 with the constant-time message "Invalid
 * email or password." for both wrong-password and unknown-account cases
 * (spec §24 — no account-existence leak). We surface that same wording.
 */
function loginErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.httpStatus === 401) return 'That email and password didn’t match. Try again.';
    if (err.httpStatus === 429) return 'Too many attempts. Please wait a minute and try again.';
    if (err.serverMessage) return err.serverMessage;
  }
  return 'We couldn’t sign you in. Please try again.';
}

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const signIn = useAuthStore((s) => s.signIn);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFields>({
    resolver: zodResolver(LoginSchema),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: LoginFields) {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const bundle = await authApi.login(values);
      signIn(bundle);
      const ret = params.get('return');
      navigate(ret && ret.startsWith('/') ? ret : '/dashboard', { replace: true });
    } catch (err) {
      setSubmitError(loginErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: '1.6rem', mb: 1 }}>
          Sign in
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Sign in to manage alerts, saved searches, and your watchlist.
        </Typography>

        {submitError ? (
          <Alert severity="error" role="alert" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate aria-label="Sign in">
          <Stack spacing={2}>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              fullWidth
              {...register('email')}
              error={Boolean(errors.email)}
              helperText={errors.email?.message ?? ' '}
              inputProps={{ 'aria-invalid': Boolean(errors.email) }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              fullWidth
              {...register('password')}
              error={Boolean(errors.password)}
              helperText={errors.password?.message ?? ' '}
              inputProps={{ 'aria-invalid': Boolean(errors.password) }}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              aria-busy={submitting}
            >
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </Stack>
        </Box>

        <Typography variant="body2" sx={{ mt: 2 }}>
          <MuiLink component={RouterLink} to="/forgot-password">
            Forgot your password?
          </MuiLink>
        </Typography>

        <Typography variant="body2" sx={{ mt: 1 }}>
          New to Round Recon?{' '}
          <MuiLink component={RouterLink} to="/register">
            Create an account
          </MuiLink>
          .
        </Typography>
      </Paper>
    </Box>
  );
}

export default LoginPage;
